// Push notifications for messages, so a phone buzzes even when the app isn't open.
// Each device that turns them on saves a token in pushTokens/{token}; the server sends to those.
import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { doc, setDoc } from 'firebase/firestore';
import { app, db } from './firebase.js';
import { S } from './store.js';
import { lsGet, lsSet } from './util.js';

const VAPID = import.meta.env.VITE_FCM_VAPID_KEY;
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const standalone = () => window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true;

/** 'on' | 'off' | 'blocked' | 'needs-home-screen' | 'unsupported' | 'not-set-up' */
export function pushState() {
  if (!VAPID) return 'not-set-up';
  if (isIOS() && !standalone()) return 'needs-home-screen';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  return Notification.permission === 'granted' && lsGet('wdr-push') === S.email ? 'on' : 'off';
}

async function register() {
  if (!(await isSupported())) throw new Error('This browser does not support notifications.');
  const reg = await navigator.serviceWorker.register('/push-sw.js');
  await navigator.serviceWorker.ready;
  const token = await getToken(getMessaging(app), { vapidKey: VAPID, serviceWorkerRegistration: reg });
  if (!token) throw new Error('No notification token.');
  await setDoc(doc(db, 'pushTokens', token), { email: S.email, at: Date.now(), ua: navigator.userAgent.slice(0, 160) });
  lsSet('wdr-push', S.email);
  return token;
}

/** Turn notifications on from a button tap (browsers only ask for permission after a tap). */
export async function enablePush() {
  const st = pushState();
  if (st === 'not-set-up') throw new Error('Notifications aren\'t set up yet. An admin needs to add the web push key.');
  if (st === 'needs-home-screen') throw new Error('On iPhone, add this app to your Home Screen first (Share, then Add to Home Screen), open it from there, and turn notifications on.');
  if (st === 'unsupported') throw new Error('This browser does not support notifications.');
  const p = await Notification.requestPermission();
  if (p !== 'granted') throw new Error('Notifications are blocked. Allow them for this site in the browser or phone settings.');
  return register();
}

/** On each sign-in, refresh this device's token quietly if notifications were turned on before. */
export function refreshPush() {
  if (pushState() === 'on') register().catch(() => {});
}
